<script>
import _throttle from 'lodash/throttle'

/** Seuil menu classique (px) — aligner $menu-expanded-min-width dans _layout.navigation.scss */
import { MENU_EXPANDED_MIN_WIDTH } from '@/layouts/navigation/layoutConstants'

const breakpoints = {
  xs: 0,
  sm: 360,
  md: 720,
  lg: 1024,
  xl: 1300
}

/**
 * Contient le contexte général d'exécution permettant de basculer le comportement de composants.
 *
 * Propriétés :
 *
 * - `screenType`: Taille de l'écran sous forme de label (xs, sm, md, lg, xl)
 * - `hasTouch`: Indique si l'écran dispose d'une fonction "tactile"
 *
 * Méthodes :
 *
 * - `up`: Retourne vrai si la taille d'écran est supérieure ou égale à celle passée en paramètre
 * - `down`: Retourne vrai si la taille d'écran est inférieure ou égale à celle passée en paramètre
 */
export default {
  name: 'UbiBrowserContext',
  data () {
    return {
      browserContext: {
        /**
         * Taille de l'écran sous forme de label (xs, sm, md, lg, xl)
         */
        screenType: null,
        viewportWidth: 0,
        /**
         * Indique si l'écran dispose d'une fonction "tactile"
         */
        hasTouch: false,
        /**
         * Retourne vrai si la taille d'écran est supérieure ou égale à celle passée en paramètre
         */
        up (breakpoint) {
          return breakpoints[breakpoint] <= breakpoints[this.screenType]
        },
        /**
         * Retourne vrai si la taille d'écran est inférieure ou égale à celle passée en paramètre
         */
        down (breakpoint) {
          return breakpoints[breakpoint] >= breakpoints[this.screenType]
        },
        isMenuExpandedViewport () {
          return this.viewportWidth >= MENU_EXPANDED_MIN_WIDTH
        },
        isLayoutBurgerVisible () {
          return (
            this.viewportWidth >= breakpoints.md &&
            this.viewportWidth < MENU_EXPANDED_MIN_WIDTH
          )
        }
      }
    }
  },
  provide () {
    return {
      browserContext: this.browserContext
    }
  },
  created () {
    if (!window) return
    this.evaluateScreenType()
    this.evaluateHasTouch()
  },
  mounted () {
    if (!window) return
    window.onresize = _throttle(this.evaluateScreenType, 250)
  },
  beforeDestroy () {
    if (!window) return
    window.onresize = null
  },
  render: function () {
    return this.$slots.default
  },
  methods: {
    evaluateScreenType () {
      const width = window.innerWidth
      let screenType = 'xs'
      for (const bp in breakpoints) {
        if (breakpoints[bp] > width) break
        screenType = bp
      }
      this.$set(this.browserContext, 'screenType', screenType)
      this.$set(this.browserContext, 'viewportWidth', width)
    },
    // From https://github.com/Modernizr/Modernizr/blob/master/feature-detects/touchevents.js
    evaluateHasTouch () {
      this.$set(
        this.browserContext,
        'hasTouch',
        'ontouchstart' in window ||
          (window.DocumentTouch && document instanceof window.DocumentTouch) ||
          window.matchMedia(
            [
              '(',
              ['', '-webkit-', '-moz-', '-o-', '-ms-'].join('touch-enabled),('),
              'heartz',
              ')'
            ].join('')
          ).matches
      )
    }
  }
}
</script>

<docs>
En ajoutant `inject: ["browserContext"]` à la définition d'un composant, il a accès à la variable `this.browserContext` qui contient les informations sur le contexte de navigation.

### Exemple

```javascript
{
  inject: ["browserContext"],
  computed: {
    screenType() {
      switch (this.browserContext.screenType) {
        case 'xs': return 'Mobile';
        case 'sm': return 'Mobile et phablet';
        case 'md': return 'Tablette portrait';
        case 'lg': return 'Tablette paysage';
        case 'xl': return 'Bureau';
      }
    },
    isLargerThanTablet() {
      return this.browserContext.up('md');
    }
  }
}
```
</docs>
